import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Upload, message } from 'antd';
import type { InputRef } from 'antd';
import { ExclamationCircleOutlined, InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { fileUpload } from '../../utils/storageUtils';

const { Dragger } = Upload;

const CONFIRM_TEXT = '确认操作代确认';
const CONFIRM_CHAR_COUNT = CONFIRM_TEXT.length;
const MAX_VOUCHER_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf'];

function isAllowedVoucherFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

type ConfirmProps = {
  open: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (payload: { voucherObjectKey: string; voucherFileName: string }) => Promise<void>;
};

export const OnBehalfExternalConfirmModal: React.FC<ConfirmProps> = ({
  open,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [confirmChars, setConfirmChars] = useState<string[]>(() => Array(CONFIRM_CHAR_COUNT).fill(''));
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [voucherObjectKey, setVoucherObjectKey] = useState<string | null>(null);
  const [voucherFileName, setVoucherFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const charInputRefs = useRef<(InputRef | null)[]>([]);
  const isComposingRef = useRef(false);

  const reset = () => {
    setConfirmChars(Array(CONFIRM_CHAR_COUNT).fill(''));
    setFileList([]);
    setVoucherObjectKey(null);
    setVoucherFileName(null);
    setUploading(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const confirmComplete = confirmChars.join('') === CONFIRM_TEXT;
  const canSubmit = Boolean(voucherObjectKey && voucherFileName && confirmComplete && !uploading);

  const handleConfirm = async () => {
    if (!voucherObjectKey || !voucherFileName) {
      message.warning('请先上传外部确认对账凭证');
      return;
    }
    if (!confirmComplete) {
      message.warning(`请逐字输入「${CONFIRM_TEXT}」后再确认`);
      return;
    }
    await onSubmit({ voucherObjectKey, voucherFileName });
    reset();
  };

  const applyConfirmChars = (chars: string[]) => {
    const next = Array(CONFIRM_CHAR_COUNT).fill('');
    for (let i = 0; i < Math.min(chars.length, CONFIRM_CHAR_COUNT); i += 1) {
      next[i] = chars[i];
    }
    setConfirmChars(next);
    const focusIndex = Math.min(chars.length, CONFIRM_CHAR_COUNT - 1);
    charInputRefs.current[focusIndex]?.focus();
  };

  const commitCharAtIndex = (index: number, value: string) => {
    const graphemes = [...value];
    if (graphemes.length === 0) {
      const next = [...confirmChars];
      next[index] = '';
      setConfirmChars(next);
      return;
    }
    if (graphemes.length > 1) {
      const merged = [...confirmChars];
      graphemes.forEach((char, offset) => {
        const targetIndex = index + offset;
        if (targetIndex < CONFIRM_CHAR_COUNT) {
          merged[targetIndex] = char;
        }
      });
      setConfirmChars(merged);
      const nextFocus = Math.min(index + graphemes.length, CONFIRM_CHAR_COUNT - 1);
      charInputRefs.current[nextFocus]?.focus();
      return;
    }

    const next = [...confirmChars];
    next[index] = graphemes[0];
    setConfirmChars(next);
    if (index < CONFIRM_CHAR_COUNT - 1) {
      charInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCharChange = (index: number, value: string) => {
    if (isComposingRef.current) {
      const next = [...confirmChars];
      next[index] = value;
      setConfirmChars(next);
      return;
    }
    commitCharAtIndex(index, value);
  };

  const handleCharCompositionEnd = (
    index: number,
    event: React.CompositionEvent<HTMLInputElement>
  ) => {
    isComposingRef.current = false;
    commitCharAtIndex(index, event.currentTarget.value);
  };

  const handleCharKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !confirmChars[index] && index > 0) {
      const next = [...confirmChars];
      next[index - 1] = '';
      setConfirmChars(next);
      charInputRefs.current[index - 1]?.focus();
    }
  };

  const uploadVoucher = async (file: File) => {
    if (!isAllowedVoucherFile(file)) {
      message.error('仅支持 PNG、JPG、PDF');
      return false;
    }
    if (file.size > MAX_VOUCHER_BYTES) {
      message.error('文件大小不能超过 10MB');
      return false;
    }

    setUploading(true);
    try {
      const { objectKey } = await fileUpload(file, 'settlement-on-behalf-external-confirm');
      setVoucherObjectKey(objectKey);
      setVoucherFileName(file.name);
      setFileList([
        {
          uid: file.name,
          name: file.name,
          status: 'done',
        },
      ]);
      message.success('凭证上传成功');
    } catch (e) {
      message.error('凭证上传失败: ' + (e as Error).message);
      setVoucherObjectKey(null);
      setVoucherFileName(null);
      setFileList([]);
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <Modal
      title={
        <span className="flex items-center gap-2 text-base font-semibold">
          <ExclamationCircleOutlined className="text-amber-500 text-lg" />
          代外部确认
        </span>
      }
      open={open}
      onCancel={handleCancel}
      width={640}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={handleCancel} disabled={!!submitting}>
            取消
          </Button>
          <Button
            type="primary"
            loading={!!submitting}
            disabled={!canSubmit || !!submitting}
            onClick={handleConfirm}
            className="!bg-amber-500 !border-amber-500 hover:!bg-amber-600 hover:!border-amber-600 disabled:!bg-amber-200 disabled:!border-amber-200 disabled:!text-white"
          >
            确认代外部确认
          </Button>
        </div>
      }
      styles={{
        body: { paddingTop: 8, paddingBottom: 8 },
      }}
    >
      <Alert
        type="error"
        showIcon={false}
        className="mb-6 !border-red-300 !bg-red-50"
        message={
          <div className="text-red-600 text-sm leading-relaxed">
            <div className="font-semibold">当前操作为【代外部确认】</div>
            <div className="mt-1">
              请上传外部确认对账的截图或者其他凭证，确认后将不可撤回。
            </div>
          </div>
        }
      />

      <div className="mb-6">
        <div className="mb-3 text-sm font-medium text-gray-800 flex items-center gap-1.5">
          <PaperClipOutlined />
          上传凭证（截图/对账单等）
        </div>
        <Dragger
          maxCount={1}
          fileList={fileList}
          disabled={uploading || !!submitting}
          beforeUpload={uploadVoucher}
          onRemove={() => {
            setFileList([]);
            setVoucherObjectKey(null);
            setVoucherFileName(null);
            return true;
          }}
          className="!bg-gray-50 hover:!border-blue-400"
        >
          <p className="ant-upload-drag-icon !mb-3">
            <InboxOutlined className="!text-4xl !text-gray-400" />
          </p>
          <p className="ant-upload-text text-base text-gray-700">
            {uploading ? '上传中...' : '点击或拖拽文件到此处上传'}
          </p>
          <p className="ant-upload-hint text-gray-500">
            支持 PNG/JPG/PDF，单个文件不超过 10MB
          </p>
        </Dragger>
      </div>

      <div>
        <div className="mb-3 text-sm text-gray-700">
          请逐字输入以下内容以确认操作：
        </div>
        <div className="mb-4 rounded-md bg-blue-50 px-4 py-3 text-center text-lg font-medium tracking-widest text-blue-600">
          {CONFIRM_TEXT}
        </div>
        <div className="flex justify-center gap-2">
          {confirmChars.map((char, index) => (
            <Input
              key={index}
              ref={(el) => {
                charInputRefs.current[index] = el;
              }}
              value={char}
              disabled={!!submitting}
              onChange={(e) => handleCharChange(index, e.target.value)}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={(e) => handleCharCompositionEnd(index, e)}
              onKeyDown={(e) => handleCharKeyDown(index, e)}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('text');
                applyConfirmChars([...pasted].slice(0, CONFIRM_CHAR_COUNT));
              }}
              className="!w-11 !h-11 text-center text-lg font-medium"
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

type RejectProps = {
  open: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (remark: string) => Promise<void>;
};

export const OnBehalfExternalRejectModal: React.FC<RejectProps> = ({
  open,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [remark, setRemark] = useState('');

  const handleCancel = () => {
    setRemark('');
    onCancel();
  };

  const handleOk = async () => {
    if (!remark.trim()) {
      message.warning('请输入驳回原因');
      return;
    }
    await onSubmit(remark.trim());
    setRemark('');
  };

  return (
    <Modal
      title={
        <span className="flex items-center gap-2 text-base font-semibold text-red-600">
          <ExclamationCircleOutlined className="text-amber-500 text-lg" />
          代外部驳回
        </span>
      }
      open={open}
      onCancel={handleCancel}
      width={640}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={handleCancel} disabled={!!submitting}>
            取消
          </Button>
          <Button
            type="primary"
            danger
            loading={!!submitting}
            disabled={!remark.trim() || !!submitting}
            onClick={handleOk}
            className="!bg-red-400 !border-red-400 hover:!bg-red-500 hover:!border-red-500 disabled:!bg-red-200 disabled:!border-red-200 disabled:!text-white"
          >
            确认代外部驳回
          </Button>
        </div>
      }
      styles={{
        body: { paddingTop: 8, paddingBottom: 8 },
      }}
    >
      <Alert
        type="error"
        showIcon={false}
        className="mb-6 !border-red-300 !bg-red-50"
        message={
          <div className="text-red-600 text-sm leading-relaxed">
            <div className="font-semibold">当前操作为【代外部驳回】</div>
            <div className="mt-1">
              请输入驳回原因，账单将回退至【申诉驳回待确认】状态。
            </div>
          </div>
        }
      />
      <div className="mb-3 text-sm font-medium text-gray-800">
        驳回原因 <span className="text-red-500">*</span>
      </div>
      <Input.TextArea
        rows={6}
        maxLength={200}
        showCount
        value={remark}
        placeholder="请输入驳回原因..."
        className="!text-base"
        onChange={(e) => setRemark(e.target.value)}
      />
    </Modal>
  );
};
